import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { findLast } from 'lodash/fp';
import queryString from 'query-string';
import {
  Chat, Channel, Thread, Window,
  MessageList, MessageInput,
} from 'stream-chat-react';
import uuidv5 from 'uuid/v5';
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText,
} from '@material-ui/core';
import { fetchClazzMembersRequest } from '../../members/actions';
import { fetchClazzRequest } from '../../clazz/actions';
import { setRegisterUsersRequest } from '../../users/actions';
import chatClientInstance from '../../common/services/chatClient';
import chatTranslateInstance from '../../common/services/chatTranslate';
import { doFileUploadRequest } from '../../common/services/validateFile';
import { fetchClasstalkUser } from '../../common/services/worktimeService';
import CustomAttachment from '../components/CustomAttachment';
import { AppBar } from '../components';
import { Spinner } from '../../common/components';
import {
  MESSAGE_ACTIONS_CLASSTALK,
} from '../../common/constants';
import { isOutOfWorktime, nl2br } from '../../common/utils';

import 'stream-chat-react/dist/css/v2/index.css';
import '../chats.css';

const S = {
  Wrapper: styled.div`
    display: flex;
    width: 100%;
  `,
  AppBar: styled(AppBar)`
    position: fixed;
    width: 100%;
    box-shadow: 0 1px 0 0 rgba(0, 0, 0, 0.03), 0 7px 9px 0 rgba(0, 0, 0, 0.03);
    background-color: #fff;
    z-index: 50;
  `,
  ButtonLeaveMessage: styled(Button)`
    color: rgba(0, 0, 0, 0.54);
    background-color: #fff;
  `,
  ButtonConfirm: styled(Button)`
    color: #fff;
    background-color: #00c896;
    &:hover {
      background-color: #00b386;
    }
  `,
};

const UUID_NAME_SPACE = process.env.REACT_APP_UUID_NAME_SPACE;

const mapStateToProps = (state) => ({
  clazz: state.clazz,
  members: state.members,
  users: state.users,
});

const mapDispatchToProps = {
  fetchClazz: fetchClazzRequest,
  fetchClazzMembers: fetchClazzMembersRequest,
  setRegisterUsers: setRegisterUsersRequest,
};

class Chats extends Component {
  state = {
    chatClient: null,
    channel: null,
    isRegistered: null,
    modalOpen: false,
    outOfWorktime: false,
  };

  componentDidMount() {
    const {
      fetchClazz, fetchClazzMembers, match,
    } = this.props;

    fetchClazz({ cid: match.params.cid });
    fetchClazzMembers({ cid: match.params.cid });
  }

  async componentDidUpdate(prevProps, prevState) {
    const { users, clazz, members } = this.props;

    if (users.isLoadingUserMe || clazz.isLoadingClazz || members.isLoadingClazzMembers) {
      return;
    }

    const { channel, isRegistered } = this.state;

    if (prevState.isRegistered === null && isRegistered === null) {
      await this.setUser();
    }

    if (prevState.channel === null && channel === null) {
      await this.setChannel();
    }
  }

  setUser = async () => {
    const {
      users, members, setRegisterUsers, location, history,
    } = this.props;

    const { mid } = queryString.parse(location.search);
    const member = findLast(['id', mid])(members.clazzMembers);

    try {
      setRegisterUsers([{
        id: users.info.id,
        name: users.info.name,
        profile_image: users.info.profile_image,
      }, member]);

      this.setState({ isRegistered: true });
    } catch (error) {
      this.setState({ isRegistered: false });

      history.push({
        pathname: '/',
      });
    }
  }

  setChannel = async () => {
    const {
      clazz,
      members,
      users,
      history,
      location,
    } = this.props;

    const { mid } = queryString.parse(location.search);
    const member = findLast(['id', mid])(members.clazzMembers);
    const userInfo = users.info;
    const memberMe = findLast(['id', userInfo.id])(members.clazzMembers);
    const clazzInfo = clazz.clazz;

    if (!(mid && member && memberMe)) {
      history.push({
        pathname: '/',
      });
      return;
    }

    const chatClient = chatClientInstance.getChatClient();
    const channelID = this.getChannelID(memberMe, member);
    const channelName = this.getChannelName(memberMe, member);

    // temporary code
    await new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      const creator = memberMe;
      const receiver = member;
      const channel = await chatClient.channel('classtalk', channelID, {
        image: clazzInfo.profile_image,
        name: channelName,
        members: [userInfo.id, member.id],
        clazz: {
          name: clazzInfo.name,
          profile_image: clazzInfo.profile_image,
          [creator.role]: creator,
          [receiver.role]: receiver,
          participants: [creator, receiver],
        },
      });

      await channel.watch();

      this.setState({
        chatClient,
        channel,
      });

      await this.checkWorktime(memberMe, member);
    } catch (error) {
      history.push({
        pathname: '/',
      });
    }
  }

  checkWorktime = async (memberMe, member) => {
    if (member.role !== 'teacher') return;

    const teacherId = member.id;

    try {
      const response = await fetchClasstalkUser(teacherId);
      const classtalkUser = response.data;
      const outOfWork = isOutOfWorktime(classtalkUser);

      this.setState({
        outOfWorktime: outOfWork,
        modalOpen: outOfWork,
      });
    } catch (error) {
      // fallback: worktime 조회 실패 시 제한 없이 허용
    }
  }

  handleAlertClose = () => {
    this.setState({ modalOpen: false });
  }

  handleAlertConfirm = () => {
    const { history } = this.props;
    this.setState({ modalOpen: false });
    history.push({ pathname: '/' });
  }

  handleMessageSubmit = (message) => {
    const { channel, outOfWorktime } = this.state;
    channel.sendMessage(
      { ...message, silent: outOfWorktime },
      { skip_push: outOfWorktime },
    );
  }

  getChannelID = (memberMe, member) => {
    const { match } = this.props;
    const channelID = memberMe.role === 'teacher'
      ? `${match.params.cid}-${memberMe.id}-${member.id}`
      : `${match.params.cid}-${member.id}-${memberMe.id}`;

    return uuidv5(channelID, UUID_NAME_SPACE);
  }

  getChannelName = (memberMe, member) => (
    memberMe.role === 'teacher'
      ? `${memberMe.name} / ${member.name}`
      : `${member.name} / ${memberMe.name}`
  )

  render() {
    const {
      users, location,
    } = this.props;
    const {
      chatClient, channel, modalOpen,
    } = this.state;
    const { lng } = queryString.parse(location.search);
    const i18n = chatTranslateInstance.getChatTranslate();
    const i18nInstance = i18n.geti18Instance();

    if (lng) chatTranslateInstance.setLanguage(lng);
    if (users.isLoadingChatTokenMe) return (<Spinner />);
    if (!channel) return (<Spinner />);

    const searchParams = new URLSearchParams(location.search);
    const platform = searchParams.get('platform');
    const isMobile = platform === 'mobile';

    return (
      <S.Wrapper>
        <S.AppBar />
        <Chat client={chatClient} theme="messaging light" i18nInstance={i18n}>
          <Channel
            channel={channel}
            Attachment={CustomAttachment}
            imageAttachmentSizeHandler={(attachment) => ({
              url: attachment.image_url || attachment.thumb_url,
            })}
          >
            <Window>
              <MessageList
                messageActions={MESSAGE_ACTIONS_CLASSTALK}
                unsafeHTML
              />
              <MessageInput
                disableMentions
                doFileUploadRequest={doFileUploadRequest}
                doImageUploadRequest={doFileUploadRequest}
                overrideSubmitHandler={this.handleMessageSubmit}
                maxRows={4}
                grow
                shouldSubmit={isMobile ? () => false : undefined}
              />
            </Window>
            <Thread />
          </Channel>
        </Chat>
        <Dialog
          open={modalOpen}
          onClose={this.handleAlertClose}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogContent style={{ marginTop: '20px' }}>
            <DialogContentText id="alert-dialog-description">
              { nl2br(i18nInstance.t('MESSAGE_OUT_OF_OFFICE')) }
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <S.ButtonLeaveMessage onClick={this.handleAlertClose}>
              { i18nInstance.t('MESSAGE_LEAVE_MESSAGE') }
            </S.ButtonLeaveMessage>
            <S.ButtonConfirm onClick={this.handleAlertConfirm}>
              { i18nInstance.t('MESSAGE_CONFIRM') }
            </S.ButtonConfirm>
          </DialogActions>
        </Dialog>
      </S.Wrapper>
    );
  }
}

Chats.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      cid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  fetchClazz: PropTypes.func.isRequired,
  fetchClazzMembers: PropTypes.func.isRequired,
  setRegisterUsers: PropTypes.func.isRequired,
  clazz: PropTypes.shape({
    isLoadingClazz: PropTypes.bool,
    clazz: PropTypes.shape({
      name: PropTypes.string.isRequired,
      profile_image: PropTypes.string.isRequired,
    }),
  }).isRequired,
  members: PropTypes.shape({
    isLoadingClazzMembers: PropTypes.bool,
    clazzMembers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  users: PropTypes.shape({
    isLoadingUserMe: PropTypes.bool,
    isLoadingChatTokenMe: PropTypes.bool.isRequired,
    info: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      profile_image: PropTypes.string,
    }),
  }).isRequired,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(Chats);
